/**
 * SavePasskeyPrompt
 *
 * Floating banner shown to a freshly signed-in user who has no passkey
 * registered yet, on a device that supports a platform authenticator
 * (Face ID / Touch ID / Windows Hello). The user can either save a passkey
 * one tap, or dismiss for 30 days.
 *
 * Lives in both Layout (employee + admin) and ManagerLayout so it surfaces
 * across the whole portal.
 */
import { useEffect, useState } from "react";
import { Fingerprint, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import {
  dismissPasskeyPrompt,
  hasLocalPasskeyHint,
  listMyPasskeys,
  markPasskeyRegistered,
  registerPasskey,
  shouldPromptSavePasskey,
} from "@/lib/passkeys";

export function SavePasskeyPrompt() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      // small delay so this never competes with the install / notifications
      // prompts in the first few seconds after login.
      await new Promise((r) => setTimeout(r, 4000));
      if (cancelled) return;
      const should = await shouldPromptSavePasskey();
      if (!should) return;
      if (hasLocalPasskeyHint()) return;
      try {
        const existing = await listMyPasskeys();
        if (existing.length > 0) {
          markPasskeyRegistered();
          return;
        }
      } catch (err) {
        // List call failing is non-fatal — we'll just show the prompt
        // and let registration re-confirm if a passkey already exists.
        console.warn("[SavePasskeyPrompt] passkey listing failed:", err);
      }
      if (!cancelled) setShow(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!show) return null;

  const onSave = async () => {
    setBusy(true);
    try {
      await registerPasskey();
      markPasskeyRegistered();
      toast.success("Passkey saved — next time, just use Face / Touch ID");
      setShow(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      if (detail && !/cancelled/i.test(detail)) toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const onDismiss = () => {
    dismissPasskeyPrompt();
    setShow(false);
  };

  return (
    <div
      className="fixed inset-x-3 bottom-3 z-40 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm pwa-install-hidden"
      data-testid="save-passkey-prompt"
    >
      <div className="rounded-2xl border border-[#C9A84C]/40 bg-white shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-[#1A2744] to-[#263A63] px-4 py-3 text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Fingerprint className="h-5 w-5 text-[#C9A84C]" />
            <div className="text-sm font-semibold">Skip typing next time</div>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-md p-1 text-white/70 hover:text-white hover:bg-white/10"
            aria-label="Dismiss"
            data-testid="dismiss-save-passkey"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-[#1A2744]">
            Save a passkey on this device so you can sign in with Face ID,
            Touch ID, or Windows Hello — no more remembering your PIN.
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={onSave}
              disabled={busy}
              className="flex-1 bg-[#1A2744] hover:bg-[#263A63] text-white"
              data-testid="confirm-save-passkey"
            >
              {busy ? "Waiting…" : "Save passkey"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onDismiss}
              disabled={busy}
              className="text-gray-600"
            >
              Not now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
